











































import {
  defineComponent,
  ref,
  defineAsyncComponent,
  computed,
  PropType,
} from '@vue/composition-api';
import { Select, Badge, SelectOptionType } from '@hb/coffee';
import { PeriodOption } from '@/views_v2/apply-product/types';
import { local } from '@/shared/utils/storage';
import store from '@/store';
import { ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import { ubtSendError } from '@/shared/utils/ubt';
import { popPageManager } from '@/shared/tools/popPageManager';
import { PopPage } from '@/shared/tools/popPageManager/types';
import LongPeriodPopup from '../long-period-popup/index.vue';

const SelectView = defineAsyncComponent(() => Promise.resolve(Select.view));

/** 阻断切换至更短的还款期限的记录key，疲劳度：每天一次 */
const BLOCK_SELECT_SHORT_PERIOD_RECORD_KEY = 'BLOCK_SELECT_SHORT_PERIOD_RECORD_KEY';

export default defineComponent({
  components: {
    SelectView,
    Badge,
    LongPeriodPopup,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    periodList: {
      type: Array as PropType<PeriodOption[]>,
      default: () => [],
    },
    currentSelectPeriod: {
      type: Object as PropType<PeriodOption>,
      default: () => ({}),
    },
    isSimplifiedListStyle: {
      type: Boolean,
      default: false,
    },
    rateContent: {
      type: String,
      default: '',
    },
  },
  emits: ['cancel', 'updateSelectPeriod', 'updateVisible'],
  setup(props, context) {
    /** 选择还款期限选项: 12个月、9个月、6个月、3个月 */
    const selectPeriodOptions = computed(() => props.periodList.map((item, index) => ({
      key: item.value,
      value: item.label,
      index,
    })));
    const longPeriodPopupRef = ref();

    /** 当前已选中的还款期限，用于默认选中 - 修复响应性问题 */
    const defaultSelectPeriodValues = computed(() => props.currentSelectPeriod?.value || '');

    /** 选择更新还款期限时，挽留弹窗的显示状态 */
    const longPeriodPopVisible = ref(false);

    /** 打开确认选择短期期限的挽留弹窗Promise任务 */
    const confirmKeepLongPeriodTask = ref<{ resolveFn:(value: boolean) => void }>();

    /** 用户维度-拦截选择短期期限的缓存key */
    const getUserBlockSelectShortPeriodKey = () => `${BLOCK_SELECT_SHORT_PERIOD_RECORD_KEY}_${store.state.base?.userId || ''}`;

    /**
     * 是否拦截切换到短的还款期限
     * @param selectingPeriod 即将选择的还款期限
     * @returns 是否拦截切换到短的还款期限,true: 拦截,false: 不拦截
     */
    const blockSelectShortPeriod = (selectingPeriod: PeriodOption): boolean => {
      const { periodList, currentSelectPeriod } = props;

      /** 若当前选择是最高还款期限 && 切换成更短的还款期限 */
      if (currentSelectPeriod.value === periodList[0].value
        && Number(selectingPeriod.value) < Number(currentSelectPeriod.value)
        && !local.get(getUserBlockSelectShortPeriodKey())) {
        return true;
      }

      return false;
    };

    const onCancel = () => {
      context.emit('cancel');
    };

    /** 打开挽留长期的确认弹窗，并返回等待用户选择结果的Promise */
    const openConfirmKeepLongPeriod = async (): Promise<boolean> => {
      longPeriodPopVisible.value = true;

      ubtSetupModuleExpose('finance_loan_longItemPopup');

      return new Promise((resolve) => {
        confirmKeepLongPeriodTask.value = { resolveFn: resolve };
      });
    };

    /** 选择还款期限前，检查是否拦截切换选择期限 */
    const onBeforeChecked = async (checkedOption: SelectOptionType) => {
      const selectingPeriod = props.periodList.find(
        (item) => item.value === checkedOption.key,
      ) as PeriodOption;

      /** 检查是否可以拦截 */
      if (blockSelectShortPeriod(selectingPeriod)) {
        longPeriodPopVisible.value = true;
        /** 注册至弹窗管理器，用户主动退出页面时，自动关闭弹窗 */
        popPageManager.addShowedPop(longPeriodPopupRef.value as unknown as PopPage);

        /** 保存阻断行为记录，缓存有效期限是当天：23:59:59 */
        local.set(getUserBlockSelectShortPeriodKey(), 1, 0, new Date().setHours(23, 59, 59, 999));

        if (await openConfirmKeepLongPeriod()) {
          context.emit('updateVisible', false);
          return false;
        }
      }

      context.emit('updateSelectPeriod', selectingPeriod);
      return true;
    };

    /** 挽留弹窗-用户继续选择还款长期 */
    const onKeepLongPeriod = () => {
      longPeriodPopVisible.value = false;

      if (typeof confirmKeepLongPeriodTask.value?.resolveFn === 'function') {
        confirmKeepLongPeriodTask.value.resolveFn(true);
        // 清理Promise引用
        confirmKeepLongPeriodTask.value = undefined;
      } else {
        ubtSendError(new Error('confirmKeepLongPeriodTask.resolveFn is not a function'));
      }
    };

    /** 挽留弹窗-用户放弃选择还款长期 */
    const onForgoLongPeriod = () => {
      longPeriodPopVisible.value = false;

      if (typeof confirmKeepLongPeriodTask.value?.resolveFn === 'function') {
        confirmKeepLongPeriodTask.value.resolveFn(false);
        // 清理Promise引用
        confirmKeepLongPeriodTask.value = undefined;
      } else {
        ubtSendError(new Error('confirmKeepLongPeriodTask.resolveFn is not a function'));
      }
    };

    return {
      longPeriodPopupRef,
      defaultSelectPeriodValues,
      selectPeriodOptions,
      longPeriodPopVisible,
      onKeepLongPeriod,
      onForgoLongPeriod,
      onBeforeChecked,
      onCancel,
    };
  },
});
